import { z } from 'zod';
import { trainingKnowledgePublishedArtifactSnapshotSchema } from './training-knowledge-published-artifact.contract.js';
export const trainingCatalogCapability = 'training.catalog.v1';
export const trainingContentReleasesCapability = 'training.content-releases.v1';
export const trainingCitationsCapability = 'training.citations.v1';
export const trainingCourseIdSchema = z.string().uuid();
export const trainingReleaseIdSchema = z.string().uuid();
export const trainingCommandIdSchema = z.string().uuid();
export const trainingCourseSlugSchema = z.string().trim().min(2).max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const trainingContentKeySchema = z.string().trim().min(2).max(120)
    .regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/);
export const trainingDifficultySchema = z.enum(['beginner', 'intermediate', 'advanced']);
export const trainingContentOriginSchema = z.enum(['platform-release', 'tenant-release']);
export const trainingCourseStatusSchema = z.enum(['draft', 'published', 'archived']);
function requireUniqueKeys(values, context, path) {
    const seen = new Set();
    values.forEach((value, index) => {
        if (seen.has(value.key)) {
            context.addIssue({ code: 'custom', message: `Duplicate ${path} key.`, path: [path, index, 'key'] });
        }
        seen.add(value.key);
    });
}
export const trainingCitationSchema = z.strictObject({
    key: trainingContentKeySchema,
    sourceTitle: z.string().trim().min(1).max(240),
    sourceUrl: z.string().url().max(1_000).nullable(),
    locator: z.string().trim().min(1).max(240),
    excerpt: z.string().trim().min(1).max(500),
    knowledgeDocumentId: z.string().uuid().nullable().optional(),
    knowledgeArtifact: trainingKnowledgePublishedArtifactSnapshotSchema.nullable().optional(),
}).superRefine((citation, context) => {
    if (citation.knowledgeArtifact !== null && citation.knowledgeArtifact !== undefined &&
        citation.knowledgeArtifact.documentId !== citation.knowledgeDocumentId) {
        context.addIssue({
            code: 'custom',
            message: 'The Knowledge publication snapshot must match the cited document.',
            path: ['knowledgeArtifact', 'documentId'],
        });
    }
});
export const trainingLessonContentSchema = z.strictObject({
    key: trainingContentKeySchema,
    title: z.string().trim().min(1).max(250),
    summary: z.string().trim().min(1).max(1_000),
    bodyMarkdown: z.string().min(1).max(100_000),
    durationMinutes: z.number().int().min(1).max(480),
    citationKeys: z.array(trainingContentKeySchema).max(40),
});
export const trainingAssessmentOptionSchema = z.strictObject({
    key: trainingContentKeySchema,
    text: z.string().trim().min(1).max(1_000),
});
export const trainingAssessmentQuestionSchema = z.strictObject({
    key: trainingContentKeySchema,
    lessonKey: trainingContentKeySchema.nullable(),
    prompt: z.string().trim().min(1).max(4_000),
    options: z.array(trainingAssessmentOptionSchema).min(2).max(10),
    correctOptionKeys: z.array(trainingContentKeySchema).min(1).max(10),
    explanation: z.string().trim().min(1).max(4_000),
    citationKeys: z.array(trainingContentKeySchema).max(20),
}).superRefine((question, context) => {
    requireUniqueKeys(question.options, context, 'options');
    const optionKeys = new Set(question.options.map(({ key }) => key));
    const correctKeys = new Set(question.correctOptionKeys);
    if (correctKeys.size !== question.correctOptionKeys.length) {
        context.addIssue({ code: 'custom', message: 'Correct option keys must be unique.', path: ['correctOptionKeys'] });
    }
    question.correctOptionKeys.forEach((key, index) => {
        if (!optionKeys.has(key)) {
            context.addIssue({ code: 'custom', message: 'Correct option is not part of the question.', path: ['correctOptionKeys', index] });
        }
    });
});
export const trainingAssessmentDefinitionSchema = z.strictObject({
    title: z.string().trim().min(1).max(250),
    passingScoreBasisPoints: z.number().int().min(1).max(10_000),
    attemptsAllowed: z.number().int().min(1).max(20),
    questionCount: z.number().int().min(1).max(100),
    questions: z.array(trainingAssessmentQuestionSchema).min(1).max(100),
}).superRefine((assessment, context) => {
    requireUniqueKeys(assessment.questions, context, 'questions');
    if (assessment.questionCount > assessment.questions.length) {
        context.addIssue({
            code: 'custom',
            message: 'Question count cannot exceed the available question bank.',
            path: ['questionCount'],
        });
    }
});
export const trainingCourseReleasePayloadSchema = z.strictObject({
    title: z.string().trim().min(1).max(250),
    description: z.string().trim().min(1).max(4_000),
    category: z.string().trim().min(1).max(120),
    difficulty: trainingDifficultySchema,
    estimatedDurationMinutes: z.number().int().min(1).max(20_000),
    mandatoryDefault: z.boolean(),
    prerequisiteCourseSlugs: z.array(trainingCourseSlugSchema).max(25),
    lessons: z.array(trainingLessonContentSchema).min(1).max(100),
    assessment: trainingAssessmentDefinitionSchema.nullable(),
    citations: z.array(trainingCitationSchema).max(250),
    internalCompletionNotice: z.string().trim().min(20).max(500),
}).superRefine((release, context) => {
    requireUniqueKeys(release.lessons, context, 'lessons');
    requireUniqueKeys(release.citations, context, 'citations');
    const lessonKeys = new Set(release.lessons.map(({ key }) => key));
    const citationKeys = new Set(release.citations.map(({ key }) => key));
    release.lessons.forEach((lesson, lessonIndex) => {
        lesson.citationKeys.forEach((key, citationIndex) => {
            if (!citationKeys.has(key))
                context.addIssue({
                    code: 'custom', message: 'Lesson citation is not declared by this release.',
                    path: ['lessons', lessonIndex, 'citationKeys', citationIndex],
                });
        });
    });
    release.assessment?.questions.forEach((question, questionIndex) => {
        if (question.lessonKey !== null && !lessonKeys.has(question.lessonKey))
            context.addIssue({
                code: 'custom', message: 'Assessment lesson is not declared by this release.',
                path: ['assessment', 'questions', questionIndex, 'lessonKey'],
            });
        question.citationKeys.forEach((key, citationIndex) => {
            if (!citationKeys.has(key))
                context.addIssue({
                    code: 'custom', message: 'Assessment citation is not declared by this release.',
                    path: ['assessment', 'questions', questionIndex, 'citationKeys', citationIndex],
                });
        });
    });
});
export const trainingCourseReleaseSchema = z.strictObject({
    id: trainingReleaseIdSchema,
    tenantId: z.string().uuid(),
    courseId: trainingCourseIdSchema,
    versionNumber: z.number().int().positive(),
    sourceReleaseId: trainingReleaseIdSchema.nullable(),
    payload: trainingCourseReleasePayloadSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(5).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const trainingCourseSchema = z.strictObject({
    id: trainingCourseIdSchema,
    tenantId: z.string().uuid(),
    slug: trainingCourseSlugSchema,
    origin: trainingContentOriginSchema,
    status: trainingCourseStatusSchema,
    activeReleaseId: trainingReleaseIdSchema.nullable(),
    latestReleaseId: trainingReleaseIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    activeRelease: trainingCourseReleaseSchema.nullable(),
    latestRelease: trainingCourseReleaseSchema,
});
export const trainingCatalogCourseSchema = z.strictObject({
    id: trainingCourseIdSchema,
    slug: trainingCourseSlugSchema,
    origin: trainingContentOriginSchema,
    status: trainingCourseStatusSchema,
    releaseId: trainingReleaseIdSchema,
    releaseVersion: z.number().int().positive(),
    title: z.string(),
    description: z.string(),
    category: z.string(),
    difficulty: trainingDifficultySchema,
    estimatedDurationMinutes: z.number().int().positive(),
    mandatoryDefault: z.boolean(),
    lessonCount: z.number().int().positive(),
    hasAssessment: z.boolean(),
    progressPercentage: z.number().int().min(0).max(100),
    progressState: z.enum(['not-started', 'in-progress', 'completed']),
});
export const trainingAssessmentMetaSchema = z.strictObject({
    title: z.string(),
    passingScoreBasisPoints: z.number().int().min(1).max(10_000),
    attemptsAllowed: z.number().int().min(1).max(20),
    questionCount: z.number().int().min(1).max(100),
});
export const trainingLearnerCourseSchema = z.strictObject({
    course: trainingCatalogCourseSchema,
    lessons: z.array(trainingLessonContentSchema),
    citations: z.array(trainingCitationSchema),
    assessment: trainingAssessmentMetaSchema.nullable(),
    internalCompletionNotice: z.string(),
    completedLessonKeys: z.array(trainingContentKeySchema),
    assessmentPassed: z.boolean(),
    progressRevision: z.number().int().nonnegative(),
});
export const trainingCourseListQuerySchema = z.strictObject({
    includeDrafts: z.boolean().default(false),
    includeArchived: z.boolean().default(false),
    search: z.string().trim().max(120).default(''),
    category: z.string().trim().max(120).nullable().default(null),
    difficulty: trainingDifficultySchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(100),
    offset: z.number().int().min(0).default(0),
});
const trainingCommandMetadataSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    idempotent: z.boolean(),
});
export const trainingCreateCourseSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    slug: trainingCourseSlugSchema,
    origin: trainingContentOriginSchema,
    release: trainingCourseReleasePayloadSchema,
    reason: z.string().trim().min(5).max(500),
});
export const trainingAppendReleaseSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    release: trainingCourseReleasePayloadSchema,
    reason: z.string().trim().min(5).max(500),
});
export const trainingPublishReleaseSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    releaseId: trainingReleaseIdSchema,
    reason: z.string().trim().min(10).max(500),
});
export const trainingRollbackReleaseSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    sourceReleaseId: trainingReleaseIdSchema,
    reason: z.string().trim().min(10).max(500),
});
export const trainingArchiveCourseSchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const trainingCourseResponseSchema = z.strictObject({
    data: trainingCourseSchema,
    command: trainingCommandMetadataSchema.optional(),
});
export const trainingCoursesResponseSchema = z.strictObject({
    data: z.array(trainingCatalogCourseSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const trainingLearnerCourseResponseSchema = z.strictObject({ data: trainingLearnerCourseSchema });
export const trainingCourseReleasesResponseSchema = z.strictObject({ data: z.array(trainingCourseReleaseSchema) });
//# sourceMappingURL=training-content.contract.js.map